/**
 * 제약사 정산 정보 상세.
 * admin-ruda-web `settlementUploadDetails.js` 포팅.
 */
import {
  useDeleteSettlementPharmaDrugs,
  useSettlementPharmaDetail,
  useSettlementPharmaDrugs,
  useUpdateSettlementPharmaStatuses,
} from '@/entities/settlement/hooks/useSettlements';
import { DataCard } from '@/shared/components/DataCard';
import { EmptyState } from '@/shared/components/EmptyState';
import { LoadingSpinner } from '@/shared/components/LoadingSpinner';
import { MenuWriteGate } from '@/shared/components/MenuWriteGate';
import { PageHeader } from '@/shared/components/PageHeader';
import { Badge } from '@/shared/components/ui/badge';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Field } from '@/shared/components/ui/form-field';
import { Input } from '@/shared/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import {
  formatDate,
  formatDateTime,
  formatSettlementYearMonth,
} from '@/pages/settlements/settlementUtils';
import { handleApiError, useToast } from '@/shared/utils/toast';
import { useNavigate, useParams, useRouter, useSearch } from '@tanstack/react-router';
import { Banknote, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';

const BULK_STATUS_OPTIONS = [
  { value: '제약사_확인_요청', label: '제약사 확인 요청' },
  { value: '제약사_정산_확정', label: '제약사 정산 확정' },
] as const;

type ConfirmAction = 'status' | 'delete' | null;

const formatNumber = (value?: number | null) =>
  value != null ? value.toLocaleString('ko-KR') : '-';

const normalizeStatusKey = (status?: string) =>
  String(status ?? '')
    .trim()
    .replace(/[\s_]+/g, '')
    .toLowerCase();

/** 제약사 정산 상태 — 상태별 배경색이 확실히 구분되도록 solid Badge variant 사용 */
const getPharmaSettlementStatusMeta = (status?: string) => {
  const key = normalizeStatusKey(status);
  switch (key) {
    case '제약사정산확정':
    case 'confirmed':
    case '승인':
      return { label: status?.trim() || '제약사 정산 확정', variant: 'success' as const };
    case '제약사확인요청':
    case '미등록거래처':
    case '흡수율미달':
      return { label: status?.trim() || '제약사 확인 요청', variant: 'warning' as const };
    case '거래불가의약품':
    case '비거래약물':
      return { label: status?.trim() || '거래 불가 의약품', variant: 'info' as const };
    case '최소금액기준미달':
    case '정산불가edi':
      return {
        label: status?.trim() || '최소금액기준 미달',
        variant: 'destructive' as const,
      };
    case 'pending':
    case '대기':
      return { label: '대기', variant: 'secondary' as const };
    default:
      return {
        label: status?.trim() || '-',
        variant: 'outline' as const,
      };
  }
};

/** 등록 상태 — 정산 상태와 겹치지 않는 색 체계 */
const getPharmaDataStatusMeta = (status?: string) => {
  const key = normalizeStatusKey(status);
  switch (key) {
    case 'uploaded':
    case '업로드':
    case '등록완료':
      return { label: status?.trim() || '업로드', variant: 'info' as const };
    case 'merged':
    case '병합완료':
    case '병합':
      return { label: status?.trim() || '병합 완료', variant: 'success' as const };
    case 'deleted':
    case '삭제':
      return { label: '삭제', variant: 'destructive' as const };
    case 'failed':
    case '실패':
      return { label: '실패', variant: 'destructive' as const };
    case 'pending':
    case '대기':
      return { label: '대기', variant: 'warning' as const };
    default:
      return {
        label: status?.trim() || '-',
        variant: 'secondary' as const,
      };
  }
};

export const SettlementUploadDetailPage: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const router = useRouter();
  const { pharmaCompanyId } = useParams({ strict: false }) as {
    pharmaCompanyId: string;
  };
  const { settlementYm, prescriptionYm } = useSearch({ strict: false }) as {
    settlementYm?: string;
    prescriptionYm?: string;
  };

  const pharmaId = Number(pharmaCompanyId);
  const settlementYearMonth = settlementYm ?? '';
  const prescriptionYearMonth = prescriptionYm ?? '';

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState('제약사_확인_요청');
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  const {
    data: detail,
    isLoading: detailLoading,
    isError: detailError,
    error: detailErrorValue,
  } = useSettlementPharmaDetail(
    settlementYearMonth || null,
    prescriptionYearMonth || null,
    Number.isFinite(pharmaId) ? pharmaId : null,
  );

  const {
    data: drugs = [],
    isLoading: drugsLoading,
    isError: drugsError,
    error: drugsErrorValue,
    refetch: refetchDrugs,
  } = useSettlementPharmaDrugs(
    settlementYearMonth || null,
    prescriptionYearMonth || null,
    Number.isFinite(pharmaId) ? pharmaId : null,
  );

  const updateStatus = useUpdateSettlementPharmaStatuses();
  const deleteDrugs = useDeleteSettlementPharmaDrugs();

  useEffect(() => {
    if (detailError && detailErrorValue) {
      handleApiError(detailErrorValue, toast);
    }
  }, [detailError, detailErrorValue, toast]);

  useEffect(() => {
    if (drugsError && drugsErrorValue) {
      handleApiError(drugsErrorValue, toast);
    }
  }, [drugsError, drugsErrorValue, toast]);

  const navigateBack = () => {
    if (router.history.canGoBack()) {
      router.history.back();
      return;
    }
    // 히스토리 없으면 목록으로 — 필터는 URL 쿼리로만 유지되므로 기본 목록 진입
    navigate({ to: ROUTE_PATHS.SETTLEMENT_UPLOAD } as never);
  };

  const toggleRow = (id: number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === drugs.length) {
      setSelectedIds([]);
      return;
    }
    setSelectedIds(drugs.map((d) => d.settlementPharmaId));
  };

  const handleDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      await deleteDrugs.mutateAsync(selectedIds);
      setSelectedIds([]);
      toast.success('성공적으로 삭제되었습니다.');
      await refetchDrugs();
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      setConfirmAction(null);
    }
  };

  const handleStatusUpdate = async () => {
    if (selectedIds.length === 0 || !bulkStatus) return;
    try {
      await updateStatus.mutateAsync({
        settlementPharmaIds: selectedIds,
        status: bulkStatus,
      });
      setSelectedIds([]);
      toast.success('상태가 성공적으로 업데이트되었습니다.');
      await refetchDrugs();
    } catch (err) {
      handleApiError(err, toast);
    } finally {
      setConfirmAction(null);
    }
  };

  const confirmTitle =
    confirmAction === 'delete'
      ? '정산 삭제'
      : bulkStatus === '제약사_확인_요청'
        ? '제약사 확인 요청'
        : '제약사 정산 확정';

  const confirmDescription =
    confirmAction === 'delete'
      ? '선택한 정산 정보가 삭제됩니다.'
      : bulkStatus === '제약사_확인_요청'
        ? '선택한 정산 정보의 상태가 제약사 확인 요청으로 변경됩니다.'
        : '선택한 정산 정보의 상태가 제약사 정산 확정으로 변경됩니다.';

  const isLoading = detailLoading || drugsLoading;
  const settlementUploadId = detail
    ? `${detail.pharmaCompanyId}-${detail.settlementYearMonth}`
    : '-';
  const resolvedPrescriptionYm =
    detail?.prescriptionYearMonth || prescriptionYearMonth || '';

  const pageHeader = (
    <PageHeader
      icon={Banknote}
      title="제약사 정산 정보 상세"
      description="제약사별 정산 기본 정보와 약품 정산 내역"
      actions={
        <>
          <MenuWriteGate>
            <Button
              variant="destructive"
              size="sm"
              disabled={selectedIds.length === 0 || deleteDrugs.isPending}
              onClick={() => setConfirmAction('delete')}
            >
              <Trash2 className="mr-2 h-4 w-4" />
              선택한 정산 삭제
            </Button>
          </MenuWriteGate>
          <Button variant="outline" size="sm" onClick={navigateBack}>
            나가기
          </Button>
        </>
      }
    />
  );

  if (!settlementYearMonth || !prescriptionYearMonth || !Number.isFinite(pharmaId)) {
    return (
      <div className="space-y-6">
        {pageHeader}
        <EmptyState description="정산 년월·처방 년월 정보가 없습니다. 목록에서 다시 진입해 주세요." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {pageHeader}

      {isLoading ? (
        <LoadingSpinner />
      ) : !detail ? (
        <EmptyState description="상세 정보를 찾을 수 없습니다." />
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle>기본 정보</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label="제약사 년 월 정산 코드">
                  <Input disabled value={settlementUploadId} />
                </Field>
                <Field label="제약사명">
                  <Input disabled value={detail.pharmaCompanyName || '-'} />
                </Field>
                <Field label="처방 년월">
                  <Input
                    disabled
                    value={formatSettlementYearMonth(resolvedPrescriptionYm)}
                  />
                </Field>
                <Field label="정산 년월">
                  <Input
                    disabled
                    value={formatSettlementYearMonth(detail.settlementYearMonth)}
                  />
                </Field>
              </div>

              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                <Field label="제약사 정산 정보 입력일시">
                  <Input disabled value={formatDateTime(detail.dataEnteredAt)} />
                </Field>
              </div>
            </CardContent>
          </Card>

          <DataCard>
            <CardContent className="space-y-4">
              <MenuWriteGate>
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={bulkStatus}
                    onValueChange={setBulkStatus}
                    disabled={selectedIds.length === 0}
                  >
                    <SelectTrigger className="w-52">
                      <SelectValue placeholder="정산 상태 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      {BULK_STATUS_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    disabled={selectedIds.length === 0 || !bulkStatus}
                    onClick={() => setConfirmAction('status')}
                  >
                    선택한 정산의 상태 설정
                  </Button>
                </div>
              </MenuWriteGate>

              {drugsLoading ? (
                <LoadingSpinner />
              ) : drugs.length === 0 ? (
                <EmptyState description="약품 정산 데이터가 없습니다." />
              ) : (
                <div className="overflow-x-auto rounded-lg border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <input
                            type="checkbox"
                            checked={
                              drugs.length > 0 && selectedIds.length === drugs.length
                            }
                            onChange={toggleAll}
                            aria-label="전체 선택"
                          />
                        </TableHead>
                        <TableHead>병의원명</TableHead>
                        <TableHead>의약품명</TableHead>
                        <TableHead>의약품 코드</TableHead>
                        <TableHead>약가</TableHead>
                        <TableHead>총 처방량</TableHead>
                        <TableHead>총 처방 금액</TableHead>
                        <TableHead>비고</TableHead>
                        <TableHead>제약사 정산 상태</TableHead>
                        <TableHead>제약사 확인 요청일</TableHead>
                        <TableHead>제약사 정산 확인일</TableHead>
                        <TableHead>등록 상태</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {drugs.map((drug) => {
                        const settlementStatus = getPharmaSettlementStatusMeta(
                          drug.pharmaCompanySettlementStatus,
                        );
                        const dataStatus = getPharmaDataStatusMeta(
                          drug.pharmaDataStatus,
                        );

                        return (
                          <TableRow key={drug.settlementPharmaId}>
                            <TableCell>
                              <input
                                type="checkbox"
                                checked={selectedIds.includes(drug.settlementPharmaId)}
                                onChange={() => toggleRow(drug.settlementPharmaId)}
                                aria-label={`${drug.drugName} 선택`}
                              />
                            </TableCell>
                            <TableCell>{drug.clinicName}</TableCell>
                            <TableCell>{drug.drugName}</TableCell>
                            <TableCell>{drug.drugCode}</TableCell>
                            <TableCell>{formatNumber(drug.price)}</TableCell>
                            <TableCell>{formatNumber(drug.totalQuantity)}</TableCell>
                            <TableCell>{formatNumber(drug.totalAmount)}</TableCell>
                            <TableCell>{drug.remarks || '-'}</TableCell>
                            <TableCell>
                              <Badge variant={settlementStatus.variant}>
                                {settlementStatus.label}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {formatDate(drug.confirmedRequestAt)}
                            </TableCell>
                            <TableCell>
                              {formatDate(drug.confirmedAt)}
                            </TableCell>
                            <TableCell>
                              <Badge variant={dataStatus.variant}>
                                {dataStatus.label}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </DataCard>

        </>
      )}

      <AlertDialog
        open={confirmAction != null}
        onOpenChange={(open) => !open && setConfirmAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirmTitle}</AlertDialogTitle>
            <AlertDialogDescription>{confirmDescription}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              onClick={
                confirmAction === 'delete' ? handleDelete : handleStatusUpdate
              }
            >
              확인
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
